'use client';

import { IPieChartLabelSmallValue } from "./PieChartLabel";
import { useFormatLabel } from "./useFormatLabel";



interface IStackedLabelProps {
  percent: number;
  smallValueThreshold: number;
  smallValuesPositions: IPieChartLabelSmallValue[];
  payload?: { key?: string;[key: string]: unknown };
  outerRadius: number;
  cx: number;
  cy: number;
  midAngle: number;
  fill: string;
  name: string;
  value: number;
}

/**
 * -----------------------------------------------------------------------------
 * Custom component to display the stacked label.
 */
export function StackedLabel({ percent, smallValueThreshold, smallValuesPositions, payload, outerRadius, cx, cy, midAngle, fill, name, value }: IStackedLabelProps) {
  const RADIAN = Math.PI / 180;

  // Format label text - show just percentage for large values, full format for small
  const formatLabel = useFormatLabel(percent, smallValueThreshold);

  // For small values, always stack them vertically at the top-left
  // Find index in pre-calculated small values array
  const smallValueIndex = smallValuesPositions.findIndex(
    (item) => item.key === payload?.key
  );

  // Calculate line end point (at the pie edge)
  const lineEndRadius = outerRadius;
  const lineEndX = cx + lineEndRadius * Math.cos(-midAngle * RADIAN);
  const lineEndY = cy + lineEndRadius * Math.sin(-midAngle * RADIAN);

  // Always position small value labels at the top-left, stacked vertically
  // Use consistent positioning regardless of angle
  const labelSpacing = 24; // Vertical spacing between labels
  const labelStartY = cy - outerRadius - 50; // Start position well above the pie

  // Calculate label Y position - use index if found, otherwise fallback
  let labelY: number;
  if (smallValueIndex >= 0) {
    // Use the pre-calculated index for consistent stacking
    labelY = labelStartY + smallValueIndex * labelSpacing;
  } else {
    // Fallback: use angle to estimate position (shouldn't happen with proper data)
    const normalizedAngle = ((midAngle % 360) + 360) % 360;
    const angleBasedIndex = Math.floor(normalizedAngle / 30);
    labelY = labelStartY + angleBasedIndex * labelSpacing;
  }

  // Calculate main vertical line position (where all branches connect)
  const mainLineX = cx - outerRadius - 12; // Fixed X position for main vertical line

  // Calculate the span of the main vertical line (from first to last label)
  const firstLabelY = labelStartY;
  const lastLabelY =
    labelStartY + (smallValuesPositions.length - 1) * labelSpacing;

  // Add spacing after main line before branches start
  const branchStartX = mainLineX + 10; // Start branches with spacing from main line

  // Position labels to the left with proper spacing
  // Position label text end point, then place horizontal line after it
  const labelTextEndX = cx - outerRadius - 50; // End position of label text (right-aligned)
  const labelX = labelTextEndX; // Label X position (text anchor end)
  const lineStartX = labelTextEndX + 8; // Start horizontal line after text with spacing

  // Check if this is the first small value to render the main vertical line
  const isFirstSmallValue = smallValueIndex === 0;
  const smallValuesCount = smallValuesPositions.length;

  return (
    <g pointerEvents="none">
      {/* Render main vertical line only once (on first small value) */}
      {isFirstSmallValue && smallValuesCount > 0 ? (
        <line
          stroke="currentColor"
          strokeOpacity={0.2}
          strokeWidth={1.5}
          x1={mainLineX}
          x2={mainLineX}
          y1={firstLabelY}
          y2={lastLabelY}
        />
      ) : null}
      {/* Horizontal line from label to main vertical line */}
      <line
        stroke="currentColor"
        strokeOpacity={0.3}
        strokeWidth={1.5}
        x1={lineStartX}
        x2={mainLineX}
        y1={labelY}
        y2={labelY}
      />
      {/* Colored branch starting from right side of main line with spacing */}
      {/* Each branch starts at its own Y position (labelY) to maintain vertical alignment */}
      <path
        d={`M ${branchStartX} ${labelY} L ${lineEndX} ${lineEndY}`}
        fill="none"
        stroke={fill}
        strokeWidth={1.5}
      />
      {/* Label text - positioned before the horizontal line, right-aligned */}
      <text
        dominantBaseline="central"
        fill={fill}
        fontSize={11}
        fontWeight={500}
        textAnchor="end"
        x={labelX}
        y={labelY}
      >
        {formatLabel(name, value)}
      </text>
    </g>
  );
}
